import { Component, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatepickerHelper } from '../../../../shared/validators/date-picker-form-validators';
import { setControlDisabled } from '../../../../shared/utils/set-control-disabled';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { CorporateHolderModel, SecurityRegistryRecord } from '@models/shares/security-registry-record';
import { SelectOption } from '@models/ui-models/select-options';
import { IStep } from '@models/ui-models/step';
import { NumbersValidators } from '../../../../shared/validators/numbers.validators';
import { CheckboxComponent } from '@shared/controls/checkbox/checkbox.component';
import { DatePickerComponent } from '@shared/controls/date-picker/date-picker.component';
import { DisclaimerComponent } from '@shared/components/commons/disclaimer/disclaimer.component';
import { DividerComponent } from '@shared/components/commons/divider/divider.component';
import { InputComponent } from '@shared/controls/input/input.component';
import { RadioComponent } from '@shared/controls/radio/radio.component';
import {
  SelectGroupsComponent,
  SelectOptionGroup
} from '@shared/controls/select-groups/select-groups.component';
import { SelectComponent } from '@shared/controls/select/select.component';
import { TextareaComponent } from '@shared/controls/textarea/textarea.component';
import {
  CompanyForm2560,
  Form2560SectionSectionOptions,
  Form2560SectionsEnum
} from '../../../models/company/company-form-2560.model';
import {
  BuyBackAttachedDocumentType,
  buyBackDocumentTypeOptions,
  BuyBackType,
  buyBackTypeOptions,
  CompanyForm280
} from '../../../models/company/company-form-280.model';
import { BaseShareFormComponent } from '../base-share-form/base-share-form.component';
import {
  ShareEstimateTableComponent
} from '../../../reusable-form-parts/share-estimate-table/share-estimate-table.component';
import { ConsiderationTypesOptions } from '../../../models/shares/company-security-issue.model';
import {
  CancellationReason,
  CompanySecurityCancellation,
  reasonsOfCancellationOptionGroups,
  ShareCancellationSteps,
  ShareCancellationStepsEnum,
} from '../../../models/shares/company-security-cancellation.model';
import {
  AuthorisedSignatoriesStepComponent
} from '../../../reusable-form-parts/authorised-signatories-step/authorised-signatories-step.component';
import {
  InputCertificateNumberComponent
} from '@shared/controls/input-certificate-number/input-certificate-number.component';
import { ClientSecurityTransaction } from '@models/security-transactions/client-security-transaction';
import {
  CompanySecurityCancellationBlock
} from '../company-security-cancellation-block/share-cancellation-block.component';
import { debounceTime, delay } from "rxjs";
import { reloadShareholdersDebounceTimeMs } from "../constants/reload-shareholders-delay";
import { StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";
import { getForm484LateFeeNotification } from "../../../utils/get-late-fee-notification";

// Share Cancellation
@Component({
  selector: 'app-share-cancellation',
  standalone: true,
  imports: [
    StepperFormComponent,
    ReactiveFormsModule,
    StepperFormDescriptionComponent,
    DividerComponent,
    InputCertificateNumberComponent,
    DatePickerComponent,
    TextareaComponent,
    InputComponent,
    SelectGroupsComponent,
    SelectComponent,
    RadioComponent,
    CheckboxComponent,
    ShareEstimateTableComponent,
    AuthorisedSignatoriesStepComponent,
    DisclaimerComponent,
    CompanySecurityCancellationBlock
  ],
  templateUrl: './company-security-cancellation.component.html',
  styleUrls: [
    './company-security-cancellation.component.scss',
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss'
  ]
})
export class CompanySecurityCancellationComponent extends BaseShareFormComponent<ShareCancellationStepsEnum, CompanySecurityCancellation> implements OnInit {
  @ViewChild(CompanySecurityCancellationBlock) shareCancellationBlock?: CompanySecurityCancellationBlock;

  override readonly StepsEnum = ShareCancellationStepsEnum;
  override readonly steps = ShareCancellationSteps;
  override currentStepIndex = 0;
  override currentStep = this.steps[this.currentStepIndex].step;
  readonly CancellationReason = CancellationReason;
  readonly reasonForCancellationOptionGroups: SelectOptionGroup[] = reasonsOfCancellationOptionGroups;
  readonly considerationTypesOptions = ConsiderationTypesOptions;

  // 280 constants
  readonly buyBackDocumentTypeOptions = buyBackDocumentTypeOptions;
  readonly buyBackTypeOptions = buyBackTypeOptions;

  // 2560 constants
  readonly form2560DocumentsOptions: SelectOption[] = Form2560SectionSectionOptions;

  transactionsList: ClientSecurityTransaction[] = [];
  stepsSignal: WritableSignal<IStep<ShareCancellationStepsEnum>[]> = signal(ShareCancellationSteps);

  override stepperForm = new FormGroup({
    [ShareCancellationStepsEnum.FormDescription]: new FormGroup({}),
    [ShareCancellationStepsEnum.ShareholderAndCancellationDetails]: new FormGroup({
      ...CompanySecurityCancellationBlock.defineFormControls(),
      issueForCashConsidarationOnly: new FormControl<boolean | null>(true, [Validators.required]),
      nonCashConsiderationDetails: new FormControl<string | null>(null, [Validators.required]),
      consolidateCertificate: new FormControl<boolean | null>(false, [Validators.required]),
      shareCertificateNumber: new FormControl<string | null>(null, [NumbersValidators.isInteger]),
      transactionDate: new FormControl<Date | null>(DatepickerHelper.getToday(), [Validators.required]),
      reason: new FormControl<CancellationReason | null>(null, [Validators.required]),
      reasonDescription: new FormControl<string | null>(null, [Validators.required]),
      section: new FormControl<string | null>(null, [Validators.required]),
    }),
    [ShareCancellationStepsEnum.Form280]: new FormGroup({
      changeDate: new FormControl<Date | null>(DatepickerHelper.getToday(), [Validators.required]),
      buyBackType: new FormControl<BuyBackType | null>(null, [Validators.required]),
      attachedDocumentType: new FormControl<BuyBackAttachedDocumentType | null>(null, [Validators.required]),
    }),
    [ShareCancellationStepsEnum.Form2560]: new FormGroup({
      dateOfProposedMeeting: new FormControl<Date | null>(DatepickerHelper.getToday(), [Validators.required]),
      section: new FormControl<Form2560SectionsEnum | null>(null, [Validators.required]),
      section249hAppliesForUnlistedCompany: new FormControl<boolean>(false),
      isListedCompany: new FormControl<boolean>(false)
    }),
    [ShareCancellationStepsEnum.AuthorisedSignatories]: new FormGroup({
      authorisedSignatories: AuthorisedSignatoriesStepComponent.defineForm(),
    }),
    [ShareCancellationStepsEnum.Estimate]: new FormGroup({}),
  });

  constructor() {
    super();
    this.redirectAfterSubmit = false;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.updateFormSteps();
    this.shareholderDetailsForm.controls.securityRegistryRecordGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), delay(1))
      .subscribe(() => this.updateFormSteps());
  }

  override setupFormChangeListeners(): void {
    super.setupFormChangeListeners();
    this.listenChangeDateChanges();
    this.listenShareBuyBackTypeChange();
    this.listenIssueForCashConsidarationOnlyChanges();
    this.listenChangeDateChange(this.shareholderDetailsForm.controls.transactionDate, getForm484LateFeeNotification);
  }

  override setCurrentStep(newStepIndex: number): void {
    if (newStepIndex < 0 || newStepIndex >= this.stepsSignal().length) {
      return;
    }

    this.currentStep = this.stepsSignal()[newStepIndex].step;
    this.currentStepIndex = newStepIndex;

    if (this.currentStep === ShareCancellationStepsEnum.Estimate && this.shouldLoadEstimate) {
      this.loadEstimates();
    }
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as CompanySecurityCancellation);
  }

  override setupChange(change: CompanySecurityCancellation = this.formModel) {
    this.shareholderDetailsForm.controls.issueForCashConsidarationOnly.setValue(this.shareholderDetailsForm.value.issueForCashConsidarationOnly ?? null);

    if (!this.isEdit && change === this.formModel) {
      return;
    }

    this.shareholderDetailsForm.controls.reason.setValue(change.reason ?? null);

    this.shareCancellationBlock!.setupChange(change);

    this.shareholderDetailsForm.patchValue({
      ...change,
      transactionDate: change.cancellation.transactionDate,
      shareCertificateNumber: change.cancellation.shareCertificateNumber,
      consolidateCertificate: change.consolidateCertificate,
      nonCashConsiderationDetails: change.nonCashConsiderationDetails
    });

    if (change.form280IfShareBuyBackOther) {
      this.form280Form.patchValue(change.form280IfShareBuyBackOther);
    }

    if (change.form2560IfCapitalReduction) {
      this.form2560Form.patchValue(change.form2560IfCapitalReduction);
    }

    this.updateFormSteps();
  }

  override buildDocument(): Document | null {
    const companySecurityCancellation = this.buildShareFormChange();
    companySecurityCancellation.description = 'Company share cancellation';

    try {
      return new Document({
        changes: [companySecurityCancellation],
        entityId: this.companyChangeData?.entityId,
        type: 'c:484',
        documentId: this.companyChangeData?.documentId,
      });
    } catch (error) {
      this.toastr.error('Failed to create Document.', 'Error');
      return null;
    }
  }

  override onEstimateLoadSuccess(estimatedSecurityRegistryRecords: SecurityRegistryRecord[]): void {
    this.estimatedSecurityRegistryRecords = estimatedSecurityRegistryRecords;
  }

  override buildShareFormChange(): CompanySecurityCancellation {
    const changeDate = this.shareholderDetailsForm.value.transactionDate ?? DatepickerHelper.getToday();

    const companySecurityCancellation = this.shareCancellationBlock!.buildShareFormChange();

    companySecurityCancellation.cancellation.transactionDate = this.shareholderDetailsForm.value.transactionDate!;
    companySecurityCancellation.cancellation.shareCertificateNumber = this.shareholderDetailsForm.value.shareCertificateNumber ?? null;

    companySecurityCancellation.cancellation.documentId = this.companyChangeData.documentId ?? null as unknown as string;
    companySecurityCancellation.cancellation.changeId = null as unknown as string;
    companySecurityCancellation.cancellation.pairTransactionId = null as unknown as string;
    companySecurityCancellation.cancellation.shareCertificateQR = null as unknown as number;

    companySecurityCancellation.reason = this.shareholderDetailsForm.value.reason!;
    companySecurityCancellation.issueForCashConsidarationOnly = this.shareholderDetailsForm.value.issueForCashConsidarationOnly ?? false,
      companySecurityCancellation.nonCashConsiderationDetails = this.shareholderDetailsForm.controls.nonCashConsiderationDetails.value ?? '',
      companySecurityCancellation.section = this.shareholderDetailsForm.value.section ?? '',
      companySecurityCancellation.reasonDescription = this.shareholderDetailsForm.value.reasonDescription ?? '',
      companySecurityCancellation.consolidateCertificate = this.shareholderDetailsForm.controls.consolidateCertificate.value ?? false,
      companySecurityCancellation.changeDate = changeDate;

    if (this.shareholderDetailsForm.value.reason === CancellationReason.BuyBackOther) {
      companySecurityCancellation.form280IfShareBuyBackOther = new CompanyForm280({
        ...this.form280Form.value as Partial<CompanyForm280>,
        changeDate
      });
    }

    if (this.shareholderDetailsForm.value.reason === CancellationReason.CapitalReductionMultipleShareholder) {
      companySecurityCancellation.form2560IfCapitalReduction = new CompanyForm2560({
        ...this.form2560Form.value as Partial<CompanyForm2560>,
        dateOfProposedMeeting: this.form2560Form.controls.dateOfProposedMeeting.value!,
        changeDate,
      });
    }

    return companySecurityCancellation;
  }

  override onShareholdersLoadSuccess(records: SecurityRegistryRecord[], isReload = false) {
    this.securityRegistryRecords = records;
    this.isLoading = false;

    this.cdr.detectChanges();
    this.fillAuthorisedSignatories();

    if (isReload) {
      const selectedSecurityRegistryRecord = this.shareholderDetailsForm.controls.securityRegistryRecordGroup.value;

      if (selectedSecurityRegistryRecord) {
        const matchingRecord = this.securityRegistryRecords.find((record) => selectedSecurityRegistryRecord[0].isEqual(record));
        const newValue = matchingRecord
          ? [matchingRecord]
          : null;

        this.shareholderDetailsForm.controls.securityRegistryRecordGroup.setValue(newValue);
      } else {
        this.shareholderDetailsForm.controls.securityRegistryRecordGroup.setValue(null);
      }
    } else {
      this.setupChange();
      this.prefillForm(this.prefillData);
    }
  }

  override isAuthorisedSignatoriesStepVisible(): boolean {
    return this.authorisedSecurityRegistryRecords.length > 0;
  }

  override fillAuthorisedSignatories(): void {
    // The Authorised Signatories tab should be present as long
    // as there is a current corporate shareholder,
    // regardless of who is cancelling shares.
    this.authorisedSecurityRegistryRecords = this.securityRegistryRecords
      .filter((record) => record.holders.some((holder) => holder.$type == CorporateHolderModel.$type));
    this.updateFormSteps();
  }

  override prefillForm(shareGroupView = this.prefillData, shouldPrefillSecurityType = this.shouldPrefillSecurityType): void {
    if (!shareGroupView?.shareholders?.length) {
      // group by class case
      this.prefillData = null;
      return;
    }

    const callRecord = shareGroupView.callRecords[0];
    const record = callRecord?.record ?? null;
    const recordSecurityHoldingId = record?.holdings?.[0]?.securityHoldingId ?? null;

    this.shareholderDetailsForm.patchValue({
      transactionDate: DatepickerHelper.getToday(),
      securityRegistryRecordGroup: record ? [record] : null,
    });

    if (this.shareCancellationBlock) {
      const selectedSecurityRegistryGroup = this.shareCancellationBlock.groupedSecurityRegistryRecords.find((group) => group.some((record) => record?.holdings.some((holder) => holder.securityHoldingId === recordSecurityHoldingId)));

      if (selectedSecurityRegistryGroup) {
        const selectedSecurityHolding = selectedSecurityRegistryGroup
          .flatMap((record) => record.holdings)?.find(holding => holding.securityHoldingId === recordSecurityHoldingId);

        if (selectedSecurityHolding !== undefined) {
          this.shareCancellationBlock.onSelectCurrentShareholder(selectedSecurityRegistryGroup, false);

          if (shouldPrefillSecurityType) {
            this.shareholderDetailsForm.controls.securityHoldingId.patchValue(selectedSecurityHolding?.securityHoldingId ?? null);
          }
        }
      }
    }

    super.prefillForm();
  }

  private updateFormSteps(): void {
    const steps = ShareCancellationSteps.filter((step) => {
      if (step.step === ShareCancellationStepsEnum.Form280) {
        return this.shareholderDetailsForm.value.reason === CancellationReason.BuyBackOther;
      }

      if (step.step === ShareCancellationStepsEnum.Form2560) {
        return this.shareholderDetailsForm.value.reason === CancellationReason.CapitalReductionMultipleShareholder;
      }

      if (step.step === ShareCancellationStepsEnum.AuthorisedSignatories) {
        return this.isAuthorisedSignatoriesStepVisible();
      }

      return true;
    });

    this.stepsSignal.set(steps);
  }

  private listenChangeDateChanges() {
    this.shareholderDetailsForm.controls.transactionDate.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(reloadShareholdersDebounceTimeMs),
      )
      .subscribe((transactionDate) => {
        if (!transactionDate) {
          return;
        }

        this.loadShareholders(transactionDate, true);
      });
  }

  private listenShareBuyBackTypeChange(): void {
    this.shareholderDetailsForm.controls.reason.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reason) => {
        setControlDisabled(this.shareholderDetailsForm.controls.reasonDescription, reason !== CancellationReason.Other);
        setControlDisabled(this.shareholderDetailsForm.controls.section, reason !== CancellationReason.Other);

        setControlDisabled(this.form280Form, reason !== CancellationReason.BuyBackOther);

        setControlDisabled(this.form2560Form, reason !== CancellationReason.CapitalReductionMultipleShareholder);

        this.updateFormSteps();
      });
  }

  private listenIssueForCashConsidarationOnlyChanges(): void {
    this.shareholderDetailsForm.controls.issueForCashConsidarationOnly.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((issueForCashConsidarationOnly) => {
        setControlDisabled(this.shareholderDetailsForm.controls.nonCashConsiderationDetails, issueForCashConsidarationOnly !== false);
      });
  }

  get shareholderDetailsForm() {
    return this.stepperForm.controls[ShareCancellationStepsEnum.ShareholderAndCancellationDetails];
  }

  get form280Form() {
    return this.stepperForm.controls[ShareCancellationStepsEnum.Form280];
  }

  get form2560Form() {
    return this.stepperForm.controls[ShareCancellationStepsEnum.Form2560];
  }

  get authorisedSignatoriesForm() {
    return this.stepperForm.controls[ShareCancellationStepsEnum.AuthorisedSignatories];
  }

  get certificatePartHidden(): boolean {
    return !this.shareCancellationBlock?.selectedShareholderShareType
      || !this.shareholderDetailsForm.controls.numberOfSharesCancelled.value
      || this.shareholderDetailsForm.controls.numberOfSharesCancelled.value >= this.shareCancellationBlock.selectedShareholderShareType.number;
  }
}
